import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { User } from "lucide-react";
import ApiService from "../../services/api";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import { WhiteLabelButton } from "../../utils/whiteLabelUtils";
import namer from "color-namer";

import "./popup.css";

interface UserDetails {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  role: string;
  two_fa_preference: "email" | "authenticator";
  clinic_id: number;
  status: string;
}

interface PasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordErrors {
  oldPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface Notification {
  id: string;
  type: "success" | "error" | "info";
  message: string;
}

// Notification Component
const NotificationToast: React.FC<{
  notification: Notification;
  onRemove: (id: string) => void;
}> = ({ notification, onRemove }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(notification.id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification.id, onRemove]);

  const getNotificationStyles = () => {
    switch (notification.type) {
      case "success":
        return "bg-green-50 border-green-200 text-green-800";
      case "error":
        return "bg-red-50 border-red-200 text-red-800";
      case "info":
        return "bg-blue-50 border-blue-200 text-blue-800";
      default:
        return "bg-gray-50 border-gray-200 text-gray-800";
    }
  };

  const getIcon = () => {
    switch (notification.type) {
      case "success":
        return (
          <svg
            className="w-5 h-5 text-green-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "error":
        return (
          <svg
            className="w-5 h-5 text-red-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "info":
        return (
          <svg
            className="w-5 h-5 text-blue-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`mb-3 p-4 rounded-lg border ${getNotificationStyles()} shadow-sm transform transition-all duration-300 ease-out`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">{getIcon()}</div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium">{notification.message}</p>
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={() => onRemove(notification.id)}
            className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Container
const NotificationContainer: React.FC<{
  notifications: Notification[];
  removeNotification: (id: string) => void;
}> = ({ notifications, removeNotification }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] w-96 space-y-2">
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>
  );
};

const UserProfileDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { contactLink, primaryColor, isWhiteLabel } = useWhiteLabel();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "password" | "2fa">(
    "profile"
  );
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Password change state
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // 2FA state
  const [selected2FA, setSelected2FA] = useState<"email" | "authenticator">(
    "email"
  );
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [show2FASetup, setShow2FASetup] = useState(false);

  // Notification functions
  const addNotification = (
    type: "success" | "error" | "info",
    message: string
  ) => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { id, type, message }]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  function getColorName(hex: string) {
    const result = namer(hex);
    return result.basic[0].name; // e.g. "lime"
  }

  // Fetch user details when dropdown opens
  useEffect(() => {
    // if (isOpen) {
    fetchUserDetails();
    // }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUserDetails = async () => {
    try {
      const data = await ApiService.getUserDetails();
      if (data.success) {
        setUserDetails(data.data);
        setSelected2FA(data.data.two_fa_preference);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      addNotification("error", "Failed to load user details");
    }
  };

  const validatePasswordField = (name: keyof PasswordForm, value: string) => {
    let error = "";

    switch (name) {
      case "oldPassword":
        if (!value) error = "Current password is required";
        break;
      case "newPassword":
        if (!value) error = "New password is required";
        else if (value.length < 6)
          error = "Password must be at least 6 characters";
        else if (!/(?=.*\d)/.test(value))
          error = "Password must contain at least one number";
        else if (value === passwordForm.oldPassword)
          error = "New password must be different from current password";
        break;
      case "confirmPassword":
        if (!value) error = "Please confirm your password";
        else if (value !== passwordForm.newPassword)
          error = "Passwords do not match";
        break;
    }

    return error;
  };

  const handlePasswordInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validatePasswordField(name as keyof PasswordForm, value);
      setPasswordErrors((prev) => ({ ...prev, [name]: error }));

      // Also revalidate confirm password if changing new password
      if (name === "newPassword" && touched.confirmPassword) {
        const confirmError = validatePasswordField(
          "confirmPassword",
          passwordForm.confirmPassword
        );
        setPasswordErrors((prev) => ({
          ...prev,
          confirmPassword: confirmError,
        }));
      }
    }
  };

  const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validatePasswordField(name as keyof PasswordForm, value);
    setPasswordErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validatePasswordForm = () => {
    const newErrors: PasswordErrors = {};

    Object.keys(passwordForm).forEach((key) => {
      const error = validatePasswordField(
        key as keyof PasswordForm,
        passwordForm[key as keyof PasswordForm]
      );
      if (error) newErrors[key as keyof PasswordErrors] = error;
    });

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({ oldPassword: true, newPassword: true, confirmPassword: true });

    if (!validatePasswordForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const data = await ApiService.changePassword(
        passwordForm.oldPassword,
        passwordForm.newPassword
      );

      if (!data.success) {
        throw new Error(data.message || "Failed to change password");
      }

      // Clear form and show success
      setPasswordForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({});
      setTouched({});
      addNotification("success", "Password changed successfully! 🔒");
      setActiveTab("profile");
    } catch (err: any) {
      setErrors({
        password:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to change password",
      });
      addNotification(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to change password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAChange = async () => {
    if (selected2FA === userDetails?.two_fa_preference) {
      setErrors({ twofa: "Please select a different 2FA method" });
      addNotification("error", "Please select a different 2FA method");
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const data = await ApiService.changeTwoFAPreference(
        selected2FA as "email" | "authenticator"
      );

      if (!data.success) {
        throw new Error(data.message || "Failed to update 2FA preference");
      }

      if (selected2FA === "authenticator" && data.data.qr_code) {
        setQrCode(data.data.qr_code);
        setSecret(data.data.secret);
        setShow2FASetup(true);
        addNotification(
          "info",
          "Please scan the QR code with your authenticator app"
        );
      } else {
        // Email 2FA - no additional setup needed
        addNotification(
          "success",
          "2FA preference updated successfully! You will receive codes via email. 📧"
        );
        fetchUserDetails(); // Refresh user details
        setActiveTab("profile");
      }
    } catch (err: any) {
      setErrors({
        twofa:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update 2FA preference",
      });
      addNotification(
        "error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update 2FA preference"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASetupComplete = () => {
    addNotification(
      "success",
      "2FA setup completed! Please use your authenticator app to generate codes when logging in. 🔐"
    );
    setShow2FASetup(false);
    setQrCode("");
    setSecret("");
    fetchUserDetails();
    setActiveTab("profile");
  };

  return (
    <>
      <NotificationContainer
        notifications={notifications}
        removeNotification={removeNotification}
      />

      <div
        className="relative w-full flex items-center justify-end"
        // ref={dropdownRef}
      >
        {/* Profile Button */}
        <User
          size={25}
          onClick={() => setIsOpen(!isOpen)}
          className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
        />
      </div>
      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="pro-popup absolute right-4 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50"
          style={{
            top: "60px",
          }}
        >
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab("profile")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === "profile"
                    ? "border-b-2"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                style={
                  activeTab === "profile"
                    ? { color: primaryColor, borderColor: primaryColor }
                    : {}
                }
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab("password")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === "password"
                    ? "border-b-2"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                style={
                  activeTab === "password"
                    ? { color: primaryColor, borderColor: primaryColor }
                    : {}
                }
              >
                Password
              </button>
              <button
                onClick={() => setActiveTab("2fa")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  activeTab === "2fa"
                    ? "border-b-2"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                style={
                  activeTab === "2fa"
                    ? { color: primaryColor, borderColor: primaryColor }
                    : {}
                }
              >
                2FA
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === "profile" && userDetails && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {userDetails.first_name} {userDetails.last_name}
                  </h3>
                  <p className="text-sm text-gray-500">{userDetails.email}</p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Phone:</span>
                    <span className="text-gray-900">
                      {userDetails.phone_number}
                    </span>
                  </div>
                  {/* <div className="flex justify-between">
                      <span className="text-gray-500">Role:</span>
                      <span className="text-gray-900 capitalize">
                        {userDetails.role}
                      </span>
                    </div> */}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span
                      className={`capitalize ${
                        userDetails.status === "approved"
                          ? "text-green-600"
                          : "text-yellow-600"
                      }`}
                    >
                      {userDetails.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">2FA Method:</span>
                    <span className="text-gray-900 capitalize">
                      {userDetails.two_fa_preference}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="w-full mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Logout
                </button>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === "password" && (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    name="oldPassword"
                    value={passwordForm.oldPassword}
                    onChange={handlePasswordInputChange}
                    onBlur={handlePasswordBlur}
                    className={`w-full px-3 py-2 border ${
                      passwordErrors.oldPassword && touched.oldPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-md focus:outline-none`}
                    // style={{ borderColor: primaryColor }}
                    required
                  />
                  {passwordErrors.oldPassword && touched.oldPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {passwordErrors.oldPassword}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordInputChange}
                    onBlur={handlePasswordBlur}
                    className={`w-full px-3 py-2 border ${
                      passwordErrors.newPassword && touched.newPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-md focus:outline-none`}
                    // style={{ borderColor: primaryColor }}
                    required
                  />
                  {passwordErrors.newPassword && touched.newPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {passwordErrors.newPassword}
                    </p>
                  )}
                  {!passwordErrors.newPassword &&
                    touched.newPassword &&
                    passwordForm.newPassword && (
                      <p className="mt-1 text-xs text-green-600">
                        Strong password!
                      </p>
                    )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordInputChange}
                    onBlur={handlePasswordBlur}
                    className={`w-full px-3 py-2 border ${
                      passwordErrors.confirmPassword && touched.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-md focus:outline-none`}
                    // style={{ borderColor: primaryColor }}
                    required
                  />
                  {passwordErrors.confirmPassword &&
                    touched.confirmPassword && (
                      <p className="mt-1 text-xs text-red-600">
                        {passwordErrors.confirmPassword}
                      </p>
                    )}
                  {!passwordErrors.confirmPassword &&
                    touched.confirmPassword &&
                    passwordForm.confirmPassword && (
                      <p className="mt-1 text-xs text-green-600">
                        Passwords match!
                      </p>
                    )}
                </div>

                {errors.password && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-md text-sm">
                    {errors.password}
                  </div>
                )}

                <WhiteLabelButton
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-4 py-2 text-white rounded-md hover:bg-gray-300 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Changing..." : "Change Password"}
                </WhiteLabelButton>
              </form>
            )}

            {/* 2FA Tab */}
            {activeTab === "2fa" && !show2FASetup && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Choose your preferred two-factor authentication method:
                </p>

                <div className="space-y-2">
                  <label
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      selected2FA === "email"
                        ? `border-${getColorName(
                            primaryColor
                          )}-500 bg-${getColorName(primaryColor)}-50`
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{
                      ...(selected2FA === "email" && {
                        borderColor: primaryColor,
                      }),
                    }}
                  >
                    <input
                      type="radio"
                      name="twofa"
                      value="email"
                      checked={selected2FA === "email"}
                      onChange={(e) =>
                        setSelected2FA(
                          e.target.value as "email" | "authenticator"
                        )
                      }
                      className="h-4 w-4"
                      style={{ accentColor: getColorName(primaryColor) }}
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-500">
                        Receive codes via email
                      </p>
                    </div>
                  </label>

                  <label
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                      selected2FA === "authenticator"
                        ? `border-${getColorName(
                            primaryColor
                          )}-500 bg-${getColorName(primaryColor)}-50`
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    style={{
                      ...(selected2FA === "authenticator" && {
                        borderColor: primaryColor,
                      }),
                    }}
                  >
                    <input
                      type="radio"
                      name="twofa"
                      value="authenticator"
                      checked={selected2FA === "authenticator"}
                      onChange={(e) =>
                        setSelected2FA(
                          e.target.value as "email" | "authenticator"
                        )
                      }
                      className="h-4 w-4"
                      style={{ accentColor: getColorName(primaryColor) }}
                    />
                    <div className="ml-3">
                      <p className="font-medium text-gray-900">
                        Authenticator App
                      </p>
                      <p className="text-sm text-gray-500">
                        Use Google Authenticator or similar
                      </p>
                    </div>
                  </label>
                </div>

                {errors.twofa && (
                  <p className="text-sm text-red-600">{errors.twofa}</p>
                )}

                <WhiteLabelButton
                  onClick={handle2FAChange}
                  disabled={
                    isLoading || selected2FA === userDetails?.two_fa_preference
                  }
                  className="w-full px-4 py-2 text-white rounded-md hover:bg-gray-300 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Updating..." : "Update 2FA Method"}
                </WhiteLabelButton>
              </div>
            )}

            {/* 2FA Setup (Authenticator) */}
            {activeTab === "2fa" && show2FASetup && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Set up Authenticator App
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Scan this QR code with your authenticator app
                  </p>
                  <img
                    src={qrCode}
                    alt="QR Code"
                    className="mx-auto border border-gray-200 rounded-lg"
                    width={200}
                    height={200}
                  />

                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <code className="text-sm font-mono break-all">
                        {secret}
                      </code>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-900 font-medium mb-2">
                      ✓ Setup Complete!
                    </p>
                    <p className="text-xs" style={{ color: primaryColor }}>
                      Your authenticator app is now configured. You'll need to
                      enter a code from your app each time you log in.
                    </p>
                  </div>
                </div>

                <button
                  onClick={handle2FASetupComplete}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Done - I've Added It
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {isOpen && <div className="backdrop" onClick={() => setIsOpen(false)} />}
    </>
  );
};

export default UserProfileDropdown;
